/**
 * Store for managing Tax Injector state.
 * Handles injector list data, records, and selection state.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  TaxInjectorSummary,
  TaxInjectorRecord,
  TaxInjectorListResponse,
  TaxInjectorRecordsResponse,
  DownloadUrlResource,
} from '@/types/taxInjector'
import type { AuthState } from '@/types/authState'
import { taxInjectorApi } from '@/services/taxInjectorService'

export const useTaxInjectorStore = defineStore('taxInjector', () => {
  // State
  const injectors = ref<TaxInjectorSummary[]>([])
  const records = ref<TaxInjectorRecord[]>([])
  const selectedInjector = ref<TaxInjectorSummary | null>(null)
  const isLoadingInjectors = ref(false)
  const isLoadingRecords = ref(false)
  const _totalElements = ref(0)
  const _currentPage = ref(0)
  const _pageSize = ref(20)

  // Getters
  const hasInjectors = computed(() => injectors.value.length > 0)
  const hasRecords = computed(() => records.value.length > 0)
  const totalElements = computed(() => _totalElements.value)
  const currentPage = computed(() => _currentPage.value)
  const pageSize = computed(() => _pageSize.value)
  const sortedInjectors = computed(() =>
    [...injectors.value].sort(
      (a, b) =>
        new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime(),
    ),
  )

  // Actions

  /**
   * Fetch all tax injectors from the API
   */
  async function fetchInjectors(
    authState: AuthState,
    allUsers?: boolean,
    email?: string,
    page?: number,
    size?: number,
    sort?: string,
  ): Promise<TaxInjectorListResponse> {
    isLoadingInjectors.value = true

    try {
      const response = await taxInjectorApi.getAll(authState, allUsers, email, page, size, sort)
      injectors.value = response.Content
      _totalElements.value = response.TotalElements
      _currentPage.value = response.Number
      _pageSize.value = response.Size
      return response
    } catch (error) {
      console.error('Error fetching tax injectors:', error)
      throw error
    } finally {
      isLoadingInjectors.value = false
    }
  }

  /**
   * Fetch records for a specific tax injector
   */
  async function fetchRecords(
    authState: AuthState,
    externalId: string,
  ): Promise<TaxInjectorRecordsResponse> {
    isLoadingRecords.value = true

    try {
      const response = await taxInjectorApi.getRecords(authState, externalId)
      records.value = response.Content
      return response
    } catch (error) {
      console.error('Error fetching tax injector records:', error)
      throw error
    } finally {
      isLoadingRecords.value = false
    }
  }

  /**
   * Find injector by external ID - useful for direct URL access
   */
  function findInjectorById(externalId: string): TaxInjectorSummary | null {
    return injectors.value.find(i => i.ExternalId === externalId) ?? null
  }

  /**
   * Set the selected injector
   */
  function setSelectedInjector(injector: TaxInjectorSummary | null): void {
    selectedInjector.value = injector
    if (!injector) {
      records.value = []
    }
  }

  /**
   * Clear records state
   */
  function clearRecords(): void {
    records.value = []
  }

  /**
   * Clear selection state
   */
  function clearSelection(): void {
    selectedInjector.value = null
    records.value = []
  }

  /**
   * Fetch a single tax injector by external ID — used for deep-link resolution
   * when the injector is not present in the current page of results
   */
  async function fetchInjectorById(
    authState: AuthState,
    externalId: string,
  ): Promise<TaxInjectorSummary> {
    try {
      return await taxInjectorApi.getById(authState, externalId)
    } catch (error) {
      console.error('Error fetching tax injector by ID:', error)
      throw error
    }
  }

  /**
   * Get download URL for a specific tax injector
   */
  async function getDownloadUrl(
    authState: AuthState,
    externalId: string,
  ): Promise<DownloadUrlResource> {
    try {
      const response = await taxInjectorApi.getDownloadUrl(authState, externalId)
      return response
    } catch (error) {
      console.error('Error getting download URL:', error)
      throw error
    }
  }

  /**
   * Clear all store state
   */
  function clearStore(): void {
    injectors.value = []
    records.value = []
    selectedInjector.value = null
    isLoadingInjectors.value = false
    isLoadingRecords.value = false
    _totalElements.value = 0
    _currentPage.value = 0
    _pageSize.value = 20
  }

  return {
    // State
    injectors,
    records,
    selectedInjector,
    isLoadingInjectors,
    isLoadingRecords,
    // Getters
    hasInjectors,
    hasRecords,
    totalElements,
    currentPage,
    pageSize,
    sortedInjectors,
    // Actions
    fetchInjectors,
    fetchInjectorById,
    fetchRecords,
    findInjectorById,
    setSelectedInjector,
    clearRecords,
    clearSelection,
    clearStore,
    getDownloadUrl,
  }
})

